import { ShowChartOutlined } from "@mui/icons-material";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { type ReactNode, useId } from "react";

import { type DateRangeValue, PopoverDateRangePicker } from "~ui/atoms/Form/DateRange/PopoverDateRangePicker";

export interface SchoolSignInsLineChartTemplateProps {
  chart: ReactNode;
  chartStatus: "error" | "pending" | "success";
  dateRange: DateRangeValue;
  dateRangeLabel: string;
  errorMessage: string;
  loadingMessage: string;
  onDateRangeChange: (value: DateRangeValue) => void;
  slideout: ReactNode;
  subtitle: string;
  title: string;
}

export function SchoolSignInsLineChartTemplate({
  chart,
  chartStatus,
  dateRange,
  dateRangeLabel,
  errorMessage,
  loadingMessage,
  onDateRangeChange,
  slideout,
  subtitle,
  title,
}: SchoolSignInsLineChartTemplateProps) {
  const loadingStatusId = useId();

  return (
    <Box>
      <Stack
        alignItems={{ xl: "center", xs: "stretch" }}
        direction={{ xl: "row", xs: "column" }}
        gap={2}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box sx={{ minWidth: 0, width: { xl: "auto", xs: "100%" } }}>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: { sm: 2, xs: 1 },
              mb: 0.5,
            }}
          >
            <ShowChartOutlined data-variant="page-header" />
            <Typography component="h2" variant="page-header">
              {title}
            </Typography>
          </Box>
          <Typography color="text.secondary" component="p" variant="subtitle1">
            {subtitle}
          </Typography>
        </Box>
        <Box sx={{ minWidth: { xl: 280 }, width: { xl: "auto", xs: "100%" } }}>
          <PopoverDateRangePicker label={dateRangeLabel} value={dateRange} onChange={onDateRangeChange} />
        </Box>
      </Stack>

      {chartStatus === "pending" ? (
        <Box sx={{ alignItems: "center", display: "flex", justifyContent: "center", minHeight: 360 }}>
          <CircularProgress aria-labelledby={loadingStatusId} />
          <Typography color="text.secondary" id={loadingStatusId} sx={{ ml: 2 }} variant="body2">
            {loadingMessage}
          </Typography>
        </Box>
      ) : chartStatus === "error" ? (
        <Typography color="error">{errorMessage}</Typography>
      ) : (
        chart
      )}

      {slideout}
    </Box>
  );
}
